<script setup lang="ts">
	import { computed } from 'vue'

	import { cn } from '@/lib/utils'

	/**
	 * Opción excluyente como tarjeta, no como radio suelto.
	 *
	 * Un radio con su etiqueta al lado deja la explicación fuera del control: la
	 * persona elige primero y lee después, si es que lee. Con la tarjeta, la
	 * consecuencia de elegir ("recibe un correo para crear su contraseña" /
	 * "alguien más reserva a su nombre") está DENTRO de lo que se hace clic.
	 *
	 * Se usa donde la decisión tiene consecuencias que conviene explicar; para
	 * un sí/no sin peso, un checkbox normal alcanza.
	 */
	interface Props {
		title: string
		description?: string
		/** Dato duro que cierra la decisión: un límite, un plazo, un precio. */
		detail?: string
		selected?: boolean
		disabled?: boolean
		name?: string
	}

	const props = defineProps<Props>()
	const emit = defineEmits<(e: 'select') => void>()

	const pick = () => {
		if (!props.disabled) emit('select')
	}

	const containerClass = computed(() =>
		cn(
			'group flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors',
			props.disabled ?
				'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60'
			: props.selected ?
				'border-[#253C70] bg-[#F5F7FC] ring-1 ring-[#253C70]'
			:	'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
		)
	)
</script>

<template>
	<button
		type="button"
		role="radio"
		:aria-checked="!!selected"
		:disabled="disabled"
		:class="containerClass"
		@click="pick"
	>
		<span
			:class="
				cn(
					'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
					selected ? 'border-[#253C70]' : 'border-gray-300'
				)
			"
			aria-hidden="true"
		>
			<span
				v-if="selected"
				class="h-2 w-2 rounded-full bg-[#253C70]"
			/>
		</span>

		<span class="min-w-0 flex-1">
			<span class="flex items-baseline justify-between gap-3">
				<span class="text-sm font-semibold text-gray-900">{{ title }}</span>
				<span
					v-if="detail"
					class="shrink-0 text-xs font-medium text-gray-500"
					>{{ detail }}</span
				>
			</span>

			<span
				v-if="description"
				class="mt-1 block text-xs leading-snug text-gray-600"
			>
				{{ description }}
			</span>
		</span>
	</button>
</template>
